import { useState, useEffect, useCallback } from 'react';
import { getTournaments, getTournamentsAllPages } from '@/api/TournamentManagement/tournamentApi';
import type { TournamentListView, TournamentListQuery } from '@/models/tournament';

export function useTournaments(query: TournamentListQuery) {
  const [data, setData] = useState<TournamentListView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getTournaments(query);
      if (result.status < 400 && result.errors.length === 0) {
        setData(result.payload);
      } else {
        setError(result.errors[0]?.description || 'An error occurred');
      }
    } catch {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  }, [query.search, query.status, query.startDateFrom, query.startDateTo, query.sortBy, query.sortDescending, query.page, query.pageSize]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { data, loading, error, refetch: fetch };
}

/**
 * Every tournament, not just the first server page. Use for pickers and filter dropdowns;
 * `useTournaments` is server-paged and clamped to 100 (see `getTournamentsAllPages`).
 */
export function useAllTournaments(query: Omit<TournamentListQuery, 'page' | 'pageSize'> = {}) {
  const [data, setData] = useState<TournamentListView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getTournamentsAllPages(query);
      if (result.status < 400 && result.errors.length === 0) {
        setData(result.payload);
      } else {
        setError(result.errors[0]?.description || 'An error occurred');
      }
    } catch {
      setError('Network error');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.search, query.status]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { data, loading, error, refetch: fetch };
}
